import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 flex items-center">
        <div className="container mx-auto px-4 max-w-md text-center">
          <div className="bg-white rounded-2xl p-8 border border-neutral-200">
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">Invalid Link</h1>
            <p className="text-neutral-600 mb-6">This password reset link is invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="btn-primary inline-block">Request New Link</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12 flex items-center" data-testid="reset-password-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <div className="bg-white rounded-2xl p-8 border border-neutral-200 animate-fade-in">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Reset Password</h1>
            <p className="text-neutral-600">Enter your new password</p>
          </div>

          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success-message">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-neutral-900">Password Reset!</h2>
              <p className="text-neutral-600">Your password has been updated. You can now log in with your new password.</p>
              <div className="pt-4 space-y-2">
                <Link to="/community-login" className="block text-primary font-semibold hover:underline">
                  Community Login
                </Link>
                <Link to="/business-login" className="block text-secondary font-semibold hover:underline">
                  Business Login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="At least 8 characters"
                  minLength={8}
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="form-label">Confirm Password</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                  placeholder="Confirm your new password"
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
